/**
 * Data Source Selection and Visual Feedback Component
 * Provides visual data source indicators and user control over data source selection
 * with real-time status updates
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Data source color coding as per requirements
const dataSourceColors = {
  'real-time': '#10b981',      // Green - Local network connection
  'near-real-time': '#f59e0b', // Orange - VPN connection  
  'database': '#ef4444',       // Red - Cached/historical data
  'demo': '#3b82f6',           // Blue - Demo/test data
  'offline': '#6b7280'         // Gray - No data available
};

const dataSourceLabels = {
  'real-time': 'Live Local',
  'near-real-time': 'Live VPN',
  'database': 'Database',
  'demo': 'Demo Data',
  'offline': 'Offline'
};

const dataSourceDescriptions = {
  'real-time': 'Direct connection to vessel SignalK server',
  'near-real-time': 'Remote connection via VPN',
  'database': 'Cached data from previous connections',
  'demo': 'Demonstration data for testing',
  'offline': 'No data source available'
};

const DataSourceIndicator = ({ 
  sourceType, 
  size = 'sm', 
  showLabel = false, 
  showTooltip = true,
  lastUpdate = null,
  dataQuality = null 
}) => {
  const color = dataSourceColors[sourceType] || dataSourceColors.offline;
  const label = dataSourceLabels[sourceType] || 'Unknown';
  const description = dataSourceDescriptions[sourceType] || 'Unknown data source';
  
  const sizeClasses = {
    xs: 'w-2 h-2',
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Never';
    const now = new Date();
    const update = new Date(timestamp);
    const diffMs = now - update;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return update.toLocaleDateString();
  };

  const tooltipContent = showTooltip ? (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
      <div className="font-medium">{label}</div>
      <div className="text-gray-300 text-xs">{description}</div>
      {lastUpdate && (
        <div className="text-gray-400 text-xs">Updated: {formatLastUpdate(lastUpdate)}</div>
      )}
      {dataQuality !== null && (
        <div className="text-gray-400 text-xs">Quality: {dataQuality}%</div>
      )}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
    </div>
  ) : null;

  return (
    <div className="relative group inline-flex items-center">
      <div 
        className={`${sizeClasses[size]} rounded-full border-2 border-white shadow-sm`}
        style={{ backgroundColor: color }}
      />
      {showLabel && (
        <span className="ml-2 text-sm font-medium text-gray-700">{label}</span>
      )}
      {tooltipContent}
    </div>
  );
};

const DataSourceSelector = ({ 
  vesselId, 
  currentSource, 
  availableSources = [], 
  onSourceChange,
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const handleSourceChange = async (newSourceType) => {
    if (isChanging || disabled) return;
    
    setIsChanging(true);
    try {
      await onSourceChange(newSourceType);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to change data source:', error);
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isChanging}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <DataSourceIndicator 
          sourceType={currentSource} 
          size="sm" 
          showTooltip={false} 
        />
        <span className="text-sm font-medium text-gray-700">
          {dataSourceLabels[currentSource] || 'Unknown'}
        </span>
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
              Available Data Sources
            </div>
            {availableSources.map((source) => (
              <button
                key={source.type}
                onClick={() => handleSourceChange(source.type)}
                disabled={isChanging || source.type === currentSource}
                className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center space-x-3">
                  <DataSourceIndicator 
                    sourceType={source.type} 
                    size="sm" 
                    showTooltip={false} 
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {dataSourceLabels[source.type]}
                    </div>
                    <div className="text-xs text-gray-500">
                      {dataSourceDescriptions[source.type]}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  {source.status && (
                    <div className="text-xs text-gray-500 capitalize">
                      {source.status}
                    </div>
                  )}
                  {source.lastUpdate && (
                    <div className="text-xs text-gray-400">
                      {new Date(source.lastUpdate).toLocaleTimeString()}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const DataSourceManager = ({ vesselId, onSourceChange, currentSource: propCurrentSource }) => {
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);
  const [currentSource, setCurrentSource] = useState(propCurrentSource || 'offline');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Update current source when prop changes
  useEffect(() => {
    if (propCurrentSource && propCurrentSource !== currentSource) {
      setCurrentSource(propCurrentSource);
    }
  }, [propCurrentSource, currentSource]);

  // Load vessel connection status and available sources
  const loadConnectionStatus = useCallback(async () => {
    try {
      // Get actual SignalK connections from the database
      const token = localStorage.getItem('token');
      const connectionsResponse = await axios.get(`/api/vessels/${vesselId}/signalk-connections`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      const connections = Array.isArray(connectionsResponse.data) ? connectionsResponse.data : [];
      const sources = [];
      
      // Add database source (always available)
      sources.push({
        type: 'database',
        status: 'available',
        lastUpdate: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(), // 7 hours ago
        dataQuality: 85,
        connectionId: 'database',
        name: 'Historical Data'
      });
      
      // Add demo source only if enabled
      const demoConnection = connections.find(conn => conn.type === 'demo' && conn.enabled);
      if (demoConnection) {
        sources.push({
          type: 'demo',
          status: 'available',
          lastUpdate: new Date().toISOString(),
          dataQuality: 100,
          connectionId: 'demo',
          name: 'Demo Data'
        });
      }

      // Check if VPN/live data is available
      try {
        const token = localStorage.getItem('token');
        const healthResponse = await axios.get('/api/health', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          timeout: 3000
        });
        
        if (healthResponse.status === 200) {
          sources.push({
            type: 'real-time',
            status: 'available',
            lastUpdate: new Date().toISOString(),
            dataQuality: 95,
            connectionId: 'live',
            name: 'Live VPN Data'
          });
        }
      } catch (liveError) {
        console.log('Live data not available:', liveError.message);
      }

      setAvailableSources(sources);
      // Only set default if no current source is set
      if (!currentSource || currentSource === 'offline') {
        setCurrentSource('database'); // Default to database
      }
      setError(null);
      
    } catch (err) {
      console.error('Failed to load connection status:', err);
      setError(err.message);
      
      // Minimal fallback
      if (!currentSource || currentSource === 'offline') {
        setCurrentSource('database');
      }
      setAvailableSources([{
        type: 'database',
        status: 'available',
        lastUpdate: new Date().toISOString(),
        dataQuality: 85,
        connectionId: 'database',
        name: 'Historical Data'
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [vesselId]);

  // Handle source change requests
  const handleSourceChange = async (newSourceType) => {
    try {
      // Find the target source
      const targetSource = availableSources.find(s => s.type === newSourceType);
      if (!targetSource) {
        throw new Error(`${newSourceType} data source not available`);
      }

      // Update current source
      setCurrentSource(newSourceType);
      
      // Notify parent component
      if (onSourceChange) {
        onSourceChange(newSourceType);
      }

      console.log(`Switched to ${newSourceType} data source`);
      
    } catch (error) {
      console.error('Failed to switch data source:', error);
      throw error;
    }
  };

  // Real-time status updates
  useEffect(() => {
    loadConnectionStatus();
    
    // Set up periodic status updates
    const interval = setInterval(loadConnectionStatus, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [loadConnectionStatus]);

  if (isLoading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-gray-300 rounded-full animate-pulse"></div>
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  if (error && availableSources.length === 0) {
    return (
      <div className="flex items-center space-x-2">
        <DataSourceIndicator sourceType="offline" size="sm" />
        <span className="text-sm text-red-600">Connection Error</span>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-4">
      <DataSourceSelector
        vesselId={vesselId}
        currentSource={currentSource}
        availableSources={availableSources}
        onSourceChange={handleSourceChange}
      />
      
      {connectionStatus && connectionStatus.dataAge !== null && (
        <div className="text-xs text-gray-500">
          Data age: {Math.floor(connectionStatus.dataAge / 1000)}s
        </div>
      )}
    </div>
  );
};

// Hook for using data source indicators in other components
export const useDataSourceIndicator = (sourceType, options = {}) => {
  return {
    color: dataSourceColors[sourceType] || dataSourceColors.offline,
    label: dataSourceLabels[sourceType] || 'Unknown',
    description: dataSourceDescriptions[sourceType] || 'Unknown data source',
    component: (props) => (
      <DataSourceIndicator 
        sourceType={sourceType} 
        {...options} 
        {...props} 
      />
    )
  };
};

export { DataSourceIndicator, DataSourceSelector };
export default DataSourceManager;